import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import RoleService from '../../services/role.service';

const RolePermissions: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [role, setRole] = useState<any>(null);
  const [permissions, setPermissions] = useState<any>({});

  useEffect(() => {
    if (id) {
      fetchRolePermissions(parseInt(id));
    }
  }, [id]);

  const fetchRolePermissions = async (roleId: number) => {
    try {
      setLoading(true);
      const response = await RoleService.getOne(roleId);
      const roleData = response.data?.role;
      
      if (roleData) {
        setRole(roleData);
        setPermissions(roleData.permissions || {});
      }
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      setError('Failed to load role permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (
    apiName: string,
    controller: string,
    action: string,
    enabled: boolean
  ) => {
    setPermissions((prev: any) => ({
      ...prev,
      [apiName]: {
        ...prev[apiName],
        controllers: {
          ...prev[apiName]?.controllers,
          [controller]: {
            ...prev[apiName]?.controllers?.[controller],
            [action]: {
              ...prev[apiName]?.controllers?.[controller]?.[action],
              enabled,
            },
          },
        },
      },
    }));
  };

  const handleSelectAll = (apiName: string, controller: string, enabled: boolean) => {
    const actions = permissions[apiName]?.controllers?.[controller];
    if (!actions) return;

    const updatedActions: any = {};
    Object.keys(actions).forEach((action) => {
      updatedActions[action] = { ...actions[action], enabled };
    });

    setPermissions((prev: any) => ({
      ...prev,
      [apiName]: {
        ...prev[apiName],
        controllers: {
          ...prev[apiName]?.controllers,
          [controller]: updatedActions,
        },
      },
    }));
  };

  const handleSave = async () => {
    if (!id) return;
    
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await RoleService.updatePermissions(parseInt(id), permissions);
      setSuccess('Permissions updated successfully!');
      
      // Redirect back to role list after 2 seconds
      setTimeout(() => {
        navigate('/roles');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const getEnabledCount = (apiName: string, controller: string) => {
    const actions = permissions[apiName]?.controllers?.[controller];
    if (!actions) return 0;
    
    return Object.values(actions).filter((action: any) => action.enabled).length;
  };

  const getTotalCount = (apiName: string, controller: string) => {
    const actions = permissions[apiName]?.controllers?.[controller];
    return actions ? Object.keys(actions).length : 0;
  };

  const formatActionName = (action: string) => {
    return action
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const formatApiName = (apiName: string) => {
    return apiName
      .replace('api::', '')
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading permissions...</Typography>
      </Box>
    );
  }

  if (!role) {
    return (
      <Box p={3}>
        <Alert severity="error">Role not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SecurityIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h4">Manage Permissions</Typography>
            <Typography variant="body2" color="text.secondary">
              {role.name} - {role.type}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/roles')}
        >
          Back to Roles
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="info">
          <Typography variant="subtitle2" gutterBottom>
            <strong>Permission Management:</strong>
          </Typography>
          <Typography variant="body2">
            Enable or disable specific API endpoints and actions for this role. Users with this role will only be able to access the enabled endpoints.
          </Typography>
        </Alert>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {Object.keys(permissions).length === 0 ? (
            <Alert severity="warning">
              No permissions available for this role. This might be a system role with default permissions.
            </Alert>
          ) : (
            Object.entries(permissions).map(([apiName, apiData]: [string, any]) => (
              <Accordion key={apiName}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center" width="100%">
                    <Typography variant="h6" flexGrow={1}>
                      {formatApiName(apiName)}
                    </Typography>
                    <Chip
                      label={`${Object.values(apiData.controllers || {}).reduce(
                        (total: number, controller: any) =>
                          total +
                          Object.values(controller).filter((action: any) => action.enabled).length,
                        0
                      )} enabled`}
                      color="primary"
                      size="small"
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {Object.entries(apiData.controllers || {}).map(
                      ([controllerName, controller]: [string, any]) => (
                        <Box key={controllerName}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Typography variant="subtitle1" fontWeight="medium">
                              {formatApiName(controllerName)}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Chip
                                label={`${getEnabledCount(apiName, controllerName)}/${getTotalCount(
                                  apiName,
                                  controllerName
                                )}`}
                                size="small"
                                variant="outlined"
                              />
                              <Button
                                size="small"
                                onClick={() =>
                                  handleSelectAll(apiName, controllerName, true)
                                }
                              >
                                Enable All
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  handleSelectAll(apiName, controllerName, false)
                                }
                              >
                                Disable All
                              </Button>
                            </Stack>
                          </Stack>
                          <Divider sx={{ mb: 2 }} />
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                              },
                              gap: 2,
                            }}
                          >
                            {Object.entries(controller).map(([actionName, action]: [string, any]) => (
                              <FormControlLabel
                                key={actionName}
                                control={
                                  <Checkbox
                                    checked={action.enabled || false}
                                    onChange={(e) =>
                                      handlePermissionChange(
                                        apiName,
                                        controllerName,
                                        actionName,
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label={formatActionName(actionName)}
                              />
                            ))}
                          </Box>
                        </Box>
                      )
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Stack>

        {Object.keys(permissions).length > 0 && (
          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
            <Button
              variant="outlined"
              onClick={() => navigate('/roles')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default RolePermissions;
